/**
 * HU-13: reverting an edit must also discard a BOM imported during that edit.
 *
 * A BOM import does not live in the client model: it is written server-side as a set of TEMP
 * workflow resources scoped by the workflow's `tempWorkflowUUID`, and every BOM read
 * (`workflowResource/{tempWorkflowUUID}[/{workflowId}]`, `billOfMaterials/temp/{tempWorkflowUUID}`)
 * returns those temp resources instead of the saved ones as long as they exist for that scope.
 * Restoring the in-memory `originalData` therefore brings back the saved nodes but keeps showing
 * the imported, unsaved BOM.
 *
 * Rebuilding the reverted workflow on a FRESH temp scope is what discards it: the new
 * `tempWorkflowUUID` has no temp resources behind it, so the same reads fall back to the workflow's
 * saved BOM. It is the same move the save already makes when it does not overwrite the data.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 */

import { getRandomUUID } from '../../generic/utilities/randomuuid'

/**
 * Historia 152 · S56 (CU-24 · `RN-01.26`, `RN-G12`) — el Revert descarta también las INTENCIONES de
 * vínculo pendientes: adoptar (`UPDATE`), proponer (`PUBLISH`), desvincular (`UNBIND`), vincular
 * (`BIND`) y copiar (`COPY`). Ninguna de ellas llegó a escribirse: viven solo en el modelo del
 * editor, entre dos guardados, y las escribe el guardado general (§8.3).
 *
 * Limpiarlas aquí es defensivo y deliberado: la foto de partida es la respuesta del servidor, que
 * nunca trae intención, pero así el Revert no depende de que nadie haya mutado esa foto por el
 * camino. Lo que NO se toca es el vínculo guardado (`state`, `code`, `versionNumber`,
 * `libraryNodeId`, `originCode`, `originVersionNumber`): revertir no desvincula nada.
 *
 * El **par de origen** (`sourceProcessCode` + `sourceProcessVersionNumber`, D12) tampoco se toca:
 * es dato PERSISTIDO e inmutable del nodo (D10, `IN8`). El que se descarta es el de los nodos
 * añadidos durante la edición, que desaparecen ENTEROS con ella —con las dos mitades del par, que
 * nunca se separan (`IN16`, S56)—: la foto de partida es la respuesta del servidor y en ella esos
 * nodos no existían.
 *
 * @param {Object} node Un nodo del workflow restaurado (ya clonado)
 */
const discardPendingOperationLink = (node) => {
  if (!node || typeof node !== 'object') return
  if (node.operationLink && typeof node.operationLink === 'object') {
    const { intent, publish, sourceNodeId, sourceWorkflowId, keepEditedContent, copiedTasks, ...persisted } = node.operationLink
    node.operationLink = persisted
  }
  node.subWorkflow?.nodes?.forEach(discardPendingOperationLink)
}

/**
 * Returns the workflow to restore on revert: the saved snapshot, deep-copied (the caller keeps
 * owning the snapshot, which must survive further edits untouched) and placed on a fresh temp
 * scope so any BOM imported during the discarded edit is no longer read back.
 * @param {Workflow} savedWorkflow The workflow as it was before the edit started
 * @returns {Workflow}
 */
const buildRevertedWorkflow = (savedWorkflow) => {
  const reverted = {
    ...structuredClone(savedWorkflow || {}),
    tempWorkflowUUID: getRandomUUID()
  }
  reverted.nodes?.forEach(discardPendingOperationLink)
  return reverted
}

export { buildRevertedWorkflow }
